import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import { CalendarDays, Sparkles } from "lucide-react";
import SpotlightCard from "../../components/ui/SpotlightCard";
import AppBackground from '../../components/ui/AppBackground.tsx';

import Button from "../../components/ui/Button";
import { Badge } from "../../components/ui/shadcn/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/shadcn/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/shadcn/tabs";
import { Input } from "../../components/ui/shadcn/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/shadcn/table";

import { getUpcomingSessionsAPI } from "../../features/upcoming_sessions/upcomingSessionAPI";
import WeekView from "./components/WeekView";
import ScheduleSessionDialog from "./components/ScheduleSessionDialog";

const toIsoZ = (date) => {
  try {
    const d = new Date(date);
    return d.toISOString();
  } catch {
    return "";
  }
};

const startOfWeekMonday = (date) => startOfWeek(date, { weekStartsOn: 1 });

export default function UpcomingSessionsPlaner() {
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);
  const [tableTab, setTableTab] = useState("all");
  const [tableQuery, setTableQuery] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    return now;
  });

  const weekDays = useMemo(() => Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i)), [weekStart]);

  const refreshSessions = async () => {
    setLoading(true);
    try {
      const res = await getUpcomingSessionsAPI({ status: "all", limit: 50 });
      setSessions(res?.sessions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSessions();
  }, []);

  const handleSlotClick = (slotDate) => {
    setSelectedSlot(slotDate);
    setOpenDialog(true);
  };

  const selectedDaySessions = useMemo(() => {
    return sessions
      .filter((s) => s?.scheduled_time && isSameDay(new Date(s.scheduled_time), selectedSlot))
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
  }, [sessions, selectedSlot]);

  const headerTitle = useMemo(() => {
    const end = addDays(weekStart, 6);
    const sameMonth = format(weekStart, "yyyy-MM") === format(end, "yyyy-MM");
    return sameMonth ? `${format(weekStart, "MMM d")} — ${format(end, "d, yyyy")}` : `${format(weekStart, "MMM d")} — ${format(end, "MMM d, yyyy")}`;
  }, [weekStart]);

  const nextUpSessions = useMemo(() => {
    const now = new Date();
    return (sessions || [])
      .filter((s) => s?.scheduled_time && new Date(s.scheduled_time) >= now)
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
      .slice(0, 4);
  }, [sessions]);

  const normalizedSessions = useMemo(() => {
    return (sessions || [])
      .filter((s) => s?.scheduled_time)
      .map((s) => ({ ...s, _dt: new Date(s.scheduled_time) }))
      .sort((a, b) => b._dt - a._dt);
  }, [sessions]);

  const tableRows = useMemo(() => {
    const now = new Date();
    const q = String(tableQuery || "").trim().toLowerCase();
    const filtered = normalizedSessions.filter((s) => {
      const isPast = s._dt < now;
      if (tableTab === "upcoming" && isPast) return false;
      if (tableTab === "past" && !isPast) return false;
      if (!q) return true;
      const hay = [
        s.title,
        s.company,
        s.type,
        s.difficulty,
        s.status,
        String(s.interview_id || ""),
        String(s.id || "")
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
    return filtered;
  }, [normalizedSessions, tableQuery, tableTab]);

  return (
    <AppBackground>
      <main className="pt-10 pb-20 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-[1600px] mx-auto space-y-6">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
                <CalendarDays className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Upcoming Sessions Planner
              </h1>
            </div>
            <p className="text-white/70">Plan interview sessions like a futuristic calendar — fast, focused, and beautiful.</p>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <SpotlightCard className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)] p-5 custom-spotlight-card" spotlightColor="rgba(92, 177, 246, 0.09)">
              <div className="relative overflow-hidden ">
                {/* <div className="absolute inset-0 opacity-25 pointer-events-none">
                  <img src={"/assets/images/Gemini_Generated_6_dlmovh.png"} className="absolute inset-0 h-full w-full object-cover scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/55 to-black/75" />
                </div> */}

                <div className="relative p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ChevronLeft"
                        iconPosition="left"
                        onClick={() => setWeekStart((d) => addDays(d, -7))}
                        className="text-gray-200 hover:text-white hover:bg-white/5"
                      >
                        Prev
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ChevronRight"
                        iconPosition="right"
                        onClick={() => setWeekStart((d) => addDays(d, 7))}
                        className="text-gray-200 hover:text-white hover:bg-white/5"
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setWeekStart(startOfWeekMonday(new Date()))}
                        className="text-white border-white/10 bg-white/5 hover:bg-white/10"
                      >
                        Today
                      </Button>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <Badge variant="outline" className="bg-white/5 border-white/10 text-white/80">
                        {headerTitle}
                      </Badge>
                      <Tabs value={view} onValueChange={setView}>
                        <TabsList className="bg-white/5 border border-white/10 rounded-2xl">
                          <TabsTrigger value="week" className="rounded-xl">
                            Week
                          </TabsTrigger>
                          <TabsTrigger value="day" className="rounded-xl">
                            Day
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                      <Button iconName="Plus" iconPosition="left" onClick={() => setOpenDialog(true)} className="shrink-0">
                        Schedule
                      </Button>
                    </div>
                  </div>

                  <div className="mt-5">
                    <Tabs value={view} onValueChange={setView}>
                      <TabsContent value="week">
                        <WeekView
                          weekStart={weekStart}
                          weekDays={weekDays}
                          sessions={sessions}
                          loading={loading}
                          selectedSlot={selectedSlot}
                          onSlotClick={handleSlotClick}
                          onSlotHover={(d) => setSelectedSlot(d)}
                        />
                      </TabsContent>
                      <TabsContent value="day">
                        <WeekView
                          weekStart={selectedSlot}
                          weekDays={[selectedSlot]}
                          sessions={sessions}
                          loading={loading}
                          selectedSlot={selectedSlot}
                          onSlotClick={handleSlotClick}
                          onSlotHover={(d) => setSelectedSlot(d)}
                          dayOnly
                        />
                      </TabsContent>
                    </Tabs>
                  </div>

                  <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-sm font-semibold text-white">Next up</div>
                      <Badge variant="outline" className="bg-white/5 border-white/10 text-white/70">
                        {nextUpSessions.length}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <AnimatePresence initial={false}>
                        {nextUpSessions.map((s) => (
                          <motion.div
                            key={s.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="rounded-3xl border border-white/10 bg-[#0B0B12]/35 p-3 hover:bg-white/10 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-white truncate">{s.title}</div>
                                <div className="text-xs text-white/60 truncate">{s.company} • {s.type}</div>
                              </div>
                              <Badge variant="outline" className="bg-white/5 border-white/10 text-white/70 shrink-0">
                                {format(new Date(s.scheduled_time), "EEE HH:mm")}
                              </Badge>
                            </div>
                            <div className="mt-2 flex items-center justify-between text-xs text-white/60">
                              <div>{s.duration} min</div>
                              <span className="inline-flex items-center gap-1">
                                <Sparkles className="h-3 w-3 text-indigo-300" />
                                {s.interview_id ? `Interview ${s.interview_id}` : "No interview"}
                              </span>
                            </div>
                          </motion.div>
                        ))}
                        {nextUpSessions.length === 0 && (
                          <motion.div
                            key="none"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            className="text-sm text-white/60 sm:col-span-2 lg:col-span-4"
                          >
                            No upcoming sessions yet.
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                      <div className="space-y-0.5">
                        <div className="text-sm font-semibold text-white">Upcoming Sessions</div>
                        <div className="text-xs text-white/60">Past + future sessions with full details</div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <Tabs value={tableTab} onValueChange={setTableTab}>
                          <TabsList className="bg-white/5 border border-white/10 rounded-2xl">
                            <TabsTrigger value="all" className="rounded-xl">All</TabsTrigger>
                            <TabsTrigger value="upcoming" className="rounded-xl">Upcoming</TabsTrigger>
                            <TabsTrigger value="past" className="rounded-xl">Past</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        <Input
                          value={tableQuery}
                          onChange={(e) => setTableQuery(e.target.value)}
                          placeholder="Search sessions..."
                          className="bg-white/5 border-white/10 text-white w-full sm:w-[320px]"
                        />
                      </div>
                    </div>

                    <div className="mt-4 rounded-3xl border border-white/10 bg-[#0B0B12]/35 overflow-hidden">
                      <Table className="text-white">
                        <TableHeader className="bg-white/5 border-b border-white/10">
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-white/70">When</TableHead>
                            <TableHead className="text-white/70">Title</TableHead>
                            <TableHead className="text-white/70">Company</TableHead>
                            <TableHead className="text-white/70">Type</TableHead>
                            <TableHead className="text-white/70">Duration</TableHead>
                            <TableHead className="text-white/70">Difficulty</TableHead>
                            <TableHead className="text-white/70">Status</TableHead>
                            <TableHead className="text-white/70">Interview</TableHead>
                            <TableHead className="text-white/70">Icon</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableRows.length === 0 ? (
                            <TableRow className="border-white/10">
                              <TableCell colSpan={9} className="py-10 text-center text-white/60">
                                No sessions found.
                              </TableCell>
                            </TableRow>
                          ) : (
                            tableRows.slice(0, 50).map((s) => {
                              const now = new Date();
                              const isPast = s._dt < now;
                              return (
                                <TableRow key={s.id} className="border-white/10 hover:bg-white/5">
                                  <TableCell className="text-white/80">
                                    <div className="flex flex-col">
                                      <span className="text-sm font-semibold">{format(s._dt, "EEE, MMM d")}</span>
                                      <span className="text-xs text-white/60">{format(s._dt, "HH:mm")}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/90 font-semibold max-w-[260px] truncate">{s.title}</TableCell>
                                  <TableCell className="text-white/80 max-w-[220px] truncate">{s.company}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="bg-white/5 border-white/10 text-white/80">
                                      {s.type}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white/80">{s.duration} min</TableCell>
                                  <TableCell className="text-white/80">{s.difficulty}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <span className={`h-2 w-2 rounded-full ${isPast ? "bg-white/40" : "bg-emerald-400/70"}`} />
                                      <span className="text-white/70">{s.status || (isPast ? "past" : "scheduled")}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-white/70">{s.interview_id || "—"}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <span className="text-white/80">{s.icon || "—"}</span>
                                      <span className="h-3 w-3 rounded-full border border-white/10" style={{ background: s.spotlight || "rgba(255,255,255,0.12)" }} />
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </SpotlightCard>
          </div>
        </div>
      </main>

      <div className="fixed right-4 bottom-20 sm:right-6 sm:bottom-6 z-30 w-[min(360px,calc(100vw-2rem))]">
        <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden" spotlightColor="rgba(139, 92, 246, 0.18)">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[rgba(30,30,46,0.78)] via-[rgba(30,30,46,0.58)] to-[rgba(46,46,69,0.30)] p-4 sm:p-5">
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <img src={"/assets/images/Gemini_Generated_1_n9djp1.png"} className="absolute inset-0 h-full w-full object-cover scale-110" />
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/55 to-black/75" />
            </div>
            <Collapsible open={tipsOpen} onOpenChange={setTipsOpen} className="relative">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">Quick Tips</div>
                  <div className="text-xs text-white/60 truncate">{format(selectedSlot, "EEE, MMM d • HH:mm")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-white/5 border-white/10 text-white/70">
                    {selectedDaySessions.length} today
                  </Badge>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="p-2 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <Sparkles className={`h-4 w-4 text-white transition-transform duration-200 ${tipsOpen ? "rotate-0" : "-rotate-90"}`} />
                    </button>
                  </CollapsibleTrigger>
                </div>
              </div>
              <CollapsibleContent>
                <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                  <div className="space-y-1.5 text-sm text-white/70">
                    <div>Click a cell to schedule quickly.</div>
                    <div>Interview settings auto-fill the session type & difficulty.</div>
                    <div>Pick a Lucide icon + color for a clean calendar look.</div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </SpotlightCard>
      </div>

      <ScheduleSessionDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        initialScheduledTime={toIsoZ(selectedSlot)}
        defaultDuration={60}
        onCreated={async () => {
          await refreshSessions();
        }}
      />
    </AppBackground>
  );
}

